import React, { useCallback, useEffect, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useOutletContext } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import {
  EmptyState,
  GlassButton,
  LoadingOverlay,
  PageHeader,
  PagePaper,
} from 'dashboard/components/common';
import NewPageModal from './NewPageModal';
import AiSiteDialog from './AiSiteDialog';
import PageEditor from './PageEditor';

/**
 * Pages list + entry to the per-page editor. Editor mode is just a
 * stateful toggle, no router push, so the parent layout stays mounted.
 */
export default function PageBuilder() {
  const [selectedBrand] = useOutletContext();
  const [pages, setPages] = useState([]);
  const [catalog, setCatalog] = useState({ regions: [], sections: [], components: [] });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  /**
   * `?page=<id>` opens straight into that page.
   *
   * Which page to edit was local state only, so nothing could link to one —
   * and the places that most want to are the ones that just made it: "design
   * how a blog post looks", from the content library, had nowhere to send
   * somebody except the list, to find it themselves.
   *
   * The parameter is cleared once used, so a reload or a Back out of the
   * editor does not drop straight back in.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const wanted = searchParams.get('page');
    if (!wanted) return;
    setEditingId(wanted);
    searchParams.delete('page');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);
  const [newPageOpen, setNewPageOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  // Hidden unless the server has a key configured. Offering a button that
  // can only fail is worse than not offering it.
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiQuota, setAiQuota] = useState(null);
  // Template previews render real copy, and that copy contains
  // {{brand_name}} — without this the cards would advertise the placeholder.
  const [brandName, setBrandName] = useState('');
  const [pageMenu, setPageMenu] = useState(null);

  // Needs the brand: availability is the server key AND this brand's switch.
  useEffect(() => {
    if (!selectedBrand) { setBrandName(''); return; }
    API.getBrandById?.({ brand_id: selectedBrand })
      .then((b) => setBrandName(b?.name || ''))
      .catch(() => setBrandName(''));
  }, [selectedBrand]);

  useEffect(() => {
    if (!selectedBrand) { setAiEnabled(false); return; }
    API.aiSiteStatus?.({ brand_id: selectedBrand })
      .then((res) => { setAiEnabled(Boolean(res?.enabled)); setAiQuota(res?.quota || null); })
      .catch(() => { setAiEnabled(false); setAiQuota(null); });
  }, [selectedBrand]);

  const loadPages = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return API.listSitePages({ brand_id: selectedBrand })
      .then((res) => setPages(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load pages'))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listSitePages({ brand_id: selectedBrand })
      .then((res) => { if (!cancelled) setPages(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load pages', { toastId: 'pagebuilder-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand]);
  useEffect(() => {
    let cancelled = false;
    API.getSiteCatalog()
      .then((res) => { if (!cancelled) setCatalog(res); })
      .catch(() => {});
    API.listSiteTemplates()
      .then((res) => { if (!cancelled) setTemplates(Array.isArray(res) ? res : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  /**
   * Put a freshly created page in the site menu.
   *
   * A page nobody can navigate to is the usual result of building one, and
   * the fix lived on a different screen entirely — Global design, under
   * header links — which is not somewhere anyone thinks to go straight after
   * naming a page.
   *
   * The menu is part of the global draft, so this leaves an unpublished
   * change rather than putting the link live. The toast says so; silently
   * publishing someone's global header as a side effect of adding a page
   * would be a much worse surprise.
   */
  const addPageToMenu = async ({ title, slug }) => {
    const path = `/${String(slug || '').replace(/^\/+/, '')}`;
    const global = await API.getSiteGlobal({ brand_id: selectedBrand });
    const links = global?.header?.links || [];
    // Nothing to do if it is already there — creating a page, deleting it and
    // creating it again should not leave two identical menu items.
    if (links.some((l) => l.url === path)) return false;
    await API.updateSiteGlobal({
      brand_id: selectedBrand,
      patch: { header: { ...(global?.header || {}), links: [...links, { label: title, url: path }] } },
    });
    return true;
  };

  const handleCreate = ({ addToMenu, ...data }) => {
    setCreating(true);
    API.createSitePage({ brand_id: selectedBrand, ...data })
      .then((created) => {
        setNewPageOpen(false);
        loadPages();
        setEditingId(created?._id);

        // The page exists either way — a menu that could not be updated is
        // worth a warning, not a failed page creation.
        if (!addToMenu || created?.isHomepage) {
          toast.success('Page created');
          return null;
        }
        return addPageToMenu({ title: data.title, slug: created?.slug || data.slug })
          .then((added) => {
            toast.success(added
              ? 'Page created and added to your menu — publish Global design to show it'
              : 'Page created');
          })
          .catch(() => toast.warning('Page created, but it could not be added to your menu'));
      })
      .catch((err) => toast.error(err?.message || 'Failed to create page'))
      .finally(() => setCreating(false));
  };

  const handleDelete = (page) => {
    if (!window.confirm(`Delete "${page.title}"?`)) return;
    API.deleteSitePage({ brand_id: selectedBrand, page_id: page._id })
      .then(() => { toast.success('Page deleted'); loadPages(); })
      .catch((err) => toast.error(err?.message || 'Failed to delete'));
  };

  if (editingId) {
    return (
      <Box sx={{ pt: CONTENT_TOP }}>
        <PageEditor
          pageId={editingId}
          brandId={selectedBrand}
          catalog={catalog}
          onBack={() => { setEditingId(null); loadPages(); }}
          onChange={loadPages}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='Pages'
          subtitle='Build the pages of your brand site. Each page has a header, main area, and footer.'
          actions={
            <Stack direction='row' spacing='8px'>
              {/* Disabled controls say why. Greyed out and silent, the next
                  question is "why?" and the screen has no answer — and here
                  the answer is one click away in the sidebar. */}
              {aiEnabled && (
                <GlassButton
                  variant='outlined'
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setAiOpen(true)}
                  disabled={!selectedBrand}
                  disabledReason='Choose a brand at the top of the sidebar first — pages belong to one brand.'
                >
                  Build with AI
                </GlassButton>
              )}
              <GlassButton
                variant='contained'
                startIcon={<AddIcon />}
                onClick={() => setNewPageOpen(true)}
                disabled={!selectedBrand}
                disabledReason='Choose a brand at the top of the sidebar first — pages belong to one brand.'
              >
                New page
              </GlassButton>
            </Stack>
          }
        />

        {!selectedBrand && <EmptyState>Select a brand to manage its pages.</EmptyState>}

        {selectedBrand && (
          <LoadingOverlay loading={loading && pages.length === 0} minHeight={200}>
            {/* Nothing here yet, and one thing to do about it.
                "Create your first one" with no button is an instruction to go
                and find a control — which on this screen is up in the bar,
                past everything else. The empty state carries the control. */}
            {!loading && pages.length === 0 && (
              <EmptyState
                action={(
                  <Button
                    variant='contained'
                    startIcon={<AddIcon />}
                    onClick={() => setNewPageOpen(true)}
                  >
                    Create your first page
                  </Button>
                )}
              >
                No pages yet. A page is where your sections, text and pictures live —
                your site needs at least one.
              </EmptyState>
            )}
            {pages.map((p) => (
              <Stack
                key={p._id}
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                onClick={() => setEditingId(p._id)}
                sx={{
                  p: '14px',
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: '12px', mb: '10px',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Stack direction='row' spacing='10px' flexWrap='wrap'>
                  <Box>
                    <Typography fontWeight='600'>{p.title}</Typography>
                    <Typography variant='caption' color='text.secondary'>/{p.slug}</Typography>
                  </Box>
                </Stack>
                <Stack direction='row' spacing='8px' alignItems='center' flexWrap='wrap'>
                  {p.isHomepage && <Chip size='small' color='info' label='Home' />}
                  <Chip
                    size='small'
                    color={p.status === 'published' ? 'success' : 'default'}
                    label={p.status}
                  />
                  <IconButton
                    size='small'
                    onClick={(e) => { e.stopPropagation(); setPageMenu({ anchor: e.currentTarget, page: p }); }}
                  >
                    <MoreVertIcon fontSize='small' />
                  </IconButton>
                </Stack>
              </Stack>
            ))}
          </LoadingOverlay>
        )}

        <Menu
          anchorEl={pageMenu?.anchor}
          open={Boolean(pageMenu)}
          onClose={() => setPageMenu(null)}
        >
          <MenuItem onClick={() => { setEditingId(pageMenu.page._id); setPageMenu(null); }}>
            Edit
          </MenuItem>
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => { handleDelete(pageMenu.page); setPageMenu(null); }}
          >
            Delete
          </MenuItem>
        </Menu>
      </PagePaper>

      <AiSiteDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        brandId={selectedBrand}
        quota={aiQuota}
        onCreated={loadPages}
      />

      <NewPageModal
        open={newPageOpen}
        onClose={() => setNewPageOpen(false)}
        onCreate={handleCreate}
        isSubmitting={creating}
        templates={templates}
        existingSlugs={pages.map((p) => p.slug).filter(Boolean)}
        brandName={brandName}
      />
    </Box>
  );
}
